/**
 * Cookie consent clean-up.
 *
 * The banner itself is TermsFeed's hosted widget (templates/includes/cookies.html.twig).
 * It blocks and later releases <script type="text/plain" cookie-consent="..."> tags, but
 * it never removes cookies a released script has already written - so withdrawing consent
 * leaves the GA identifiers in the browser. That is what this module handles.
 */

const CONSENT_COOKIE = "cookie_consent_level";

/** Cookies GA4 writes. Cleared whenever tracking consent is absent. */
const ANALYTICS_COOKIE = /^(_ga|_gid|_gat)/;

/**
 * Consent levels the visitor has granted.
 *
 * TermsFeed stores them URL-encoded as a JSON object of level => bool. A missing or
 * unparsable cookie means "not asked yet", which grants nothing.
 *
 * @returns {Set<string>}
 */
function grantedLevels() {
    const entry = document.cookie
        .split(";")
        .map((part) => part.trim())
        .find((part) => part.startsWith(`${CONSENT_COOKIE}=`));

    if (!entry) {
        return new Set();
    }

    try {
        const levels = JSON.parse(decodeURIComponent(entry.slice(CONSENT_COOKIE.length + 1)));

        return new Set(Object.keys(levels).filter((level) => levels[level]));
    } catch {
        return new Set();
    }
}

/**
 * Expires analytics cookies left behind by a visitor who has since withdrawn consent.
 *
 * GA sets _ga on the registrable domain, so each name is expired against every domain it
 * could plausibly be scoped to - a cookie written for a domain the page does not control
 * is simply ignored by the browser.
 */
function purgeAnalyticsCookies() {
    const names = document.cookie
        .split(";")
        .map((part) => part.trim().split("=")[0])
        .filter((name) => ANALYTICS_COOKIE.test(name));

    const host = window.location.hostname;
    const registrable = host.split(".").slice(-2).join(".");

    for (const name of names) {
        document.cookie = `${name}=; Max-Age=0; path=/`;

        for (const domain of [host, `.${host}`, registrable, `.${registrable}`]) {
            document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
        }
    }
}

export function initializeConsentModule() {
    if (!grantedLevels().has("tracking")) {
        purgeAnalyticsCookies();
    }
}
